import * as _ from 'lodash-es';
import React, { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import PageLayout from '../app/layout/PageLayout';
import TwoColumnRow from '../app/layout/TwoColumnRow';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import EmptyState from '../components/EmptyState';
import PageTitle from '../components/PageTitle';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';
import { PostSort } from '../graphql/generated/graphql';
import topicsIcon from '../icons/non-prefilled/16x16/allTopics.svg';
import alphabeticalIcon from '../icons/non-prefilled/16x16/alphabetical.svg';
import editIcon from '../icons/non-prefilled/16x16/edit.svg';
import memberIcon from '../icons/non-prefilled/16x16/member.svg';
import popularityIcon from '../icons/non-prefilled/16x16/popularity.svg';
import publishIcon from '../icons/non-prefilled/16x16/publish.svg';
import unorganizedIcon from '../icons/non-prefilled/16x16/unorganized.svg';
import viewedIcon from '../icons/non-prefilled/16x16/viewed.svg';
import PostFilterMenu, {
  POST_FILTER_MENU_PROPS,
  POST_SORT_MENU_PROPS,
} from '../post/PostFilterMenu';
import PostRowMeta from '../post/PostRowMeta';
import PostSectionHeader from '../post/PostSectionHeader';
import { TopicPostSectionSimpleList } from '../topic/posts/TopicPostSection';
import { useAllTopicMap } from '../topic/useAllTopics';
import { UIActions } from '../ui/uiSlice';
import ConnectionHelper from '../utils/graphql/ConnectionHelper';
import TemplatesSuggestions from './TemplatesSuggestions';
import * as styles from './TemplatesView.module.scss';

const ALLOWED_SORT_OPTIONS = [
  PostSort.Popularity,
  PostSort.Published,
  PostSort.Alphabetical,
  PostSort.Edited,
  PostSort.Viewed,
] as const;

type AllowedSortOptions = Extract<
  PostSort,
  (typeof ALLOWED_SORT_OPTIONS)[number]
>;

const isSortOptionAllowed = (sortBy: PostSort): sortBy is AllowedSortOptions =>
  ALLOWED_SORT_OPTIONS.includes(sortBy as AllowedSortOptions);

const TEMPLATES_VIEW_ALL_QUERY = gql(/** @complexity 72 */ `
  query TemplatesViewAll($first: Int = 100, $afterCursor: String, $sortBy: PostSort) {
    listPosts(filters: [TEMPLATE, UNARCHIVED, PUBLISHED], sortBy: $sortBy, first: $first, after: $afterCursor) {
      edges {
        node {
          id
          owner {
            id
          }
          contributions {
            user {
              id
            }
          }
          topics {
            id
          }
          ...TopicPostSectionSimpleList_post
          ...TemplateMetadata_post
        }
      }
      pageInfo {
        hasNextPage
        endCursor
      }
    }
  }
`);

enum TemplateCategories {
  MINE = 'MINE',
  ALL_TOPICS = 'ALL_TOPICS',
  UNORGANIZED = 'UNORGANIZED',
}

const TEMPLATE_CATEGORIES_LABELS = {
  [TemplateCategories.MINE]: 'Mine',
  [TemplateCategories.ALL_TOPICS]: 'All Topics',
  [TemplateCategories.UNORGANIZED]: 'Unorganized',
} as const;

const TEMPLATE_METADATA_ATTRIBUTE: Record<
  AllowedSortOptions,
  'popularity' | 'publishedAt' | 'editedAt' | 'userViewedAt'
> = {
  [PostSort.Popularity]: 'popularity',
  [PostSort.Published]: 'publishedAt',
  [PostSort.Alphabetical]: 'publishedAt',
  [PostSort.Edited]: 'editedAt',
  [PostSort.Viewed]: 'userViewedAt',
};

const TEMPLATE_METADATA_FRAGMENT = gql(`
  fragment TemplateMetadata_post on Post {
    id
    views
    publishedAt
    editedAt
    userViewedAt
  }
`);

interface TemplateMetadataProps {
  post: DocumentType<typeof TEMPLATE_METADATA_FRAGMENT>;
  sortBy: PostSort;
}

const TemplateMetadata = ({ post, sortBy }: TemplateMetadataProps) => {
  const { views, publishedAt, editedAt, userViewedAt } = post;

  if (!isSortOptionAllowed(sortBy)) {
    return null;
  }

  return (
    <PostRowMeta
      values={{
        popularity: views,
        publishedAt,
        editedAt,
        userViewedAt,
      }}
      attribute={TEMPLATE_METADATA_ATTRIBUTE[sortBy]}
    />
  );
};

const sortTopics = (
  topicIds: string[],
  topicMap: ReturnType<typeof useAllTopicMap>,
) =>
  _.compact(
    topicIds.map((topicId) => {
      const topic = topicMap[topicId];
      // To each topic assign a path that consists of the topic name and all its ancestors
      return topic
        ? {
            topicId,
            path: [
              topic.name,
              ...topic.ancestors.map((ancestor) => ancestor.name),
            ].toReversed(), // From the highest ancestor to the topic itself
          }
        : null;
    }),
  )
    .toSorted(
      (a, b) =>
        a.path.length !== b.path.length
          ? a.path.length - b.path.length // Prefer higher level (fewer ancestors) topics
          : a.path.join('').localeCompare(b.path.join('')), // Same level sort alphabetically
    )
    .map((item) => item.topicId);

const PAGE_COUNT = 100; // This is large because frontend filters will be applied

interface TemplatesViewProps {
  initialCategory?: TemplateCategories;
}

const TemplatesView = (props: TemplatesViewProps) => {
  const currentUser = useAppSelector((state) => state.session.user);
  const topicMap = useAllTopicMap();
  const dispatch = useAppDispatch();
  const [category, setCategory] = useState<TemplateCategories>(
    props.initialCategory ?? TemplateCategories.ALL_TOPICS,
  );
  const sortBy: AllowedSortOptions = useAppSelector((state) =>
    isSortOptionAllowed(state.ui.templatesOrder)
      ? state.ui.templatesOrder
      : PostSort.Popularity,
  );

  const templatesResponse = useQuery(TEMPLATES_VIEW_ALL_QUERY, {
    variables: { first: PAGE_COUNT, sortBy },
  });

  const { data: allTemplatesData } = templatesResponse;

  const fetchMore = ConnectionHelper.generateFetchMore(
    TEMPLATES_VIEW_ALL_QUERY,
    templatesResponse,
    'listPosts',
  );

  const allTemplates = _.compact(
    allTemplatesData?.listPosts?.edges?.map((edge) => edge?.node),
  );

  const { mine, unorganized, allTopics, current, topicIds } = useMemo(() => {
    const [withTopics, withoutTopics] = _.partition(
      allTemplates,
      (template) => template.topics.length > 0,
    );

    // For templates that are not in a topic:
    // 1. if they are mine, they are mine ;-)
    // 2. if they are not mine, they are unorganized
    const [mineTemplates, unorganizedTemplates] = _.partition(
      withoutTopics,
      (template) =>
        template.owner?.id === currentUser.id ||
        template.contributions.some(({ user }) => user.id === currentUser.id),
    );

    let currentTemplates: typeof withTopics = [];

    if (category === TemplateCategories.MINE) {
      currentTemplates = mineTemplates;
    } else if (category === TemplateCategories.ALL_TOPICS) {
      currentTemplates = withTopics;
    } else if (category === TemplateCategories.UNORGANIZED) {
      currentTemplates = unorganizedTemplates;
    }

    const topicIdsWithTemplates = _.uniq(
      currentTemplates.flatMap((template) => template.topics.map((t) => t.id)),
    );

    return {
      mine: mineTemplates.length,
      unorganized: unorganizedTemplates.length,
      allTopics: withTopics.length,
      current: currentTemplates,
      topicIds: sortTopics(topicIdsWithTemplates, topicMap),
    };
  }, [allTemplates, category, currentUser.id, topicMap]);

  const canShowPostCounts = !fetchMore;

  const controls = useMemo(() => {
    const categoriesOptions = {
      [TemplateCategories.MINE]: {
        label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.MINE],
        icon: memberIcon,
        count: mine,
      },
      [TemplateCategories.ALL_TOPICS]: {
        label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.ALL_TOPICS],
        icon: topicsIcon,
        count: allTopics,
      },
      [TemplateCategories.UNORGANIZED]: {
        label: TEMPLATE_CATEGORIES_LABELS[TemplateCategories.UNORGANIZED],
        icon: unorganizedIcon,
        count: unorganized,
      },
    };

    return (
      <>
        <PostFilterMenu<TemplateCategories>
          {...POST_FILTER_MENU_PROPS}
          onChange={setCategory}
          options={
            canShowPostCounts
              ? categoriesOptions
              : _.mapValues(categoriesOptions, (option) => ({
                  ...option,
                  count: undefined,
                }))
          }
          value={category}
        />
        <PostFilterMenu<AllowedSortOptions>
          {...POST_SORT_MENU_PROPS}
          onChange={(order) => {
            dispatch(UIActions.changeTemplatesOrder(order));
          }}
          options={{
            [PostSort.Popularity]: {
              icon: popularityIcon,
              label: 'Most Popular',
            },
            [PostSort.Published]: { icon: publishIcon, label: 'Publish Date' },
            [PostSort.Edited]: { icon: editIcon, label: 'Last Edited' },
            [PostSort.Viewed]: { icon: viewedIcon, label: 'Recent View' },
            [PostSort.Alphabetical]: {
              icon: alphabeticalIcon,
              label: 'Alphabetical',
            },
          }}
          value={sortBy}
        />
      </>
    );
  }, [
    mine,
    allTopics,
    unorganized,
    category,
    sortBy,
    canShowPostCounts,
    dispatch,
  ]);

  return (
    <PageLayout.Content noPaddingX noPaddingY>
      <div className={styles.container}>
        <TwoColumnRow
          main={
            <PageTitle
              title="Templates"
              subtitle="Create and share templates with your team, or pick one from your team’s collection to get started."
            />
          }
        />
        <TwoColumnRow
          full={
            <>
              {topicIds.length === 0 ? (
                <TopicPostSectionSimpleList
                  header={
                    <PostSectionHeader title="Posts" controls={controls} />
                  }
                  renderMeta={(post) => (
                    <TemplateMetadata post={post} sortBy={sortBy} />
                  )}
                  posts={current}
                  fetchMore={fetchMore}
                />
              ) : (
                topicIds.map((topicId, index) => {
                  const topic = topicMap[topicId];
                  if (!topic) {
                    return null;
                  }
                  return (
                    <TopicPostSectionSimpleList
                      key={topicId}
                      header={
                        <PostSectionHeader
                          topic={topic}
                          ancestors={topic.ancestors}
                          controls={index === 0 ? controls : null}
                        />
                      }
                      posts={current.filter((template) =>
                        template.topics.some((t) => t.id === topicId),
                      )}
                      renderMeta={(post) => (
                        <TemplateMetadata post={post} sortBy={sortBy} />
                      )}
                      fetchMore={
                        index === topicIds.length - 1 ? fetchMore : null
                      }
                      // Use a button instead of an infinite scroll because
                      // frontend filters can result in only a few items per page, so the
                      // infinite waypoint may always be visible in the viewport and
                      // not trigger automatic loading.
                      fetchMoreType="button"
                    />
                  );
                })
              )}
              {!templatesResponse.loading && (
                <>
                  {!current.length && (
                    <div className={styles.empty}>
                      <EmptyState imageName="templates" vertical noMargin />
                    </div>
                  )}
                  <TemplatesSuggestions />
                </>
              )}
            </>
          }
        />
      </div>
    </PageLayout.Content>
  );
};

export default TemplatesView;
